import { useState, useEffect } from 'react'
import { formatDateTime } from '../../../../shared/lib/format'
import { adminCapacityApi } from '../../../../shared/services/adminCapacityApi'

interface CollectionDataModalProps {
  isOpen: boolean
  onClose: () => void
  collectionName: string
}

const CollectionDataModal: React.FC<CollectionDataModalProps> = ({
  isOpen,
  onClose,
  collectionName,
}) => {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')
  const [fieldOrder, setFieldOrder] = useState<string[]>([])

  useEffect(() => {
    if (isOpen && collectionName) {
      fetchCollectionData()
    }
  }, [isOpen, collectionName, page])

  const fetchCollectionData = async () => {
    setLoading(true)
    setError('')
    try {
      const result = await adminCapacityApi.collection(collectionName, page)
      if (result.success) {
        setData(result.data)

        // 컬렉션별 필드 순서 정의
        const predefinedOrder = getFieldOrder(collectionName, result.data.data)
        setFieldOrder(predefinedOrder)
      } else {
        throw new Error(result.message || '데이터 조회 실패')
      }
    } catch (error: any) {
      console.error('컬렉션 데이터 조회 실패:', error)
      setError(error.message || '데이터를 불러올 수 없습니다')
    } finally {
      setLoading(false)
    }
  }

  // 컬렉션별 필드 순서를 정의하는 함수
  const getFieldOrder = (collection: string, documents: any[]): string[] => {
    if (!documents || documents.length === 0) {
      return []
    }

    // 컬렉션별 미리 정의된 필드 순서
    const predefinedOrders: { [key: string]: string[] } = {
      urls: [
        '_id',
        'originalUrl',
        'shortCode',
        'isCustom',
        'title',
        'description',
        'favicon',
        'userId',
        'isPublic',
        'tags',
        'category',
        'notes',
        'folderId',
        'isFavorite',
        'isActive',
        'clickCount',
        'qrCode',
        'createdAt',
        'updatedAt',
        '__v',
        'sharedWith',
        'teamId',
        'teamPermissions',
      ],
      users: [
        '_id',
        'username',
        'email',
        'password',
        'avatar',
        'isVerified',
        'subscription',
        'settings',
        'createdAt',
        'updatedAt',
        '__v',
      ],
      clicks: [
        '_id',
        'urlId',
        'shortCode',
        'userAgent',
        'ipAddress',
        'referer',
        'country',
        'city',
        'device',
        'browser',
        'os',
        'createdAt',
        '__v',
      ],
      folders: [
        '_id',
        'name',
        'description',
        'color',
        'icon',
        'userId',
        'parentFolder',
        'isDefault',
        'sortOrder',
        'createdAt',
        'updatedAt',
        '__v',
      ],
      tags: [
        '_id',
        'name',
        'color',
        'userId',
        'usageCount',
        'createdAt',
        'updatedAt',
        '__v',
      ],
    }

    // 미리 정의된 순서가 있으면 사용
    if (predefinedOrders[collection]) {
      const predefined = predefinedOrders[collection]
      const actualFields = Object.keys(documents[0])

      // 정의된 순서대로 정렬하되, 누락된 필드는 뒤에 추가
      const orderedFields = predefined.filter(field =>
        actualFields.includes(field)
      )
      const remainingFields = actualFields.filter(
        field => !predefined.includes(field)
      )

      return [...orderedFields, ...remainingFields]
    }

    // 미리 정의된 순서가 없으면 일반적인 필드 우선순위 적용
    const commonOrder = [
      '_id',
      'name',
      'title',
      'originalUrl',
      'shortCode',
      'email',
      'username',
    ]
    const actualFields = Object.keys(documents[0])
    const orderedFields = commonOrder.filter(field =>
      actualFields.includes(field)
    )
    const remainingFields = actualFields.filter(
      field => !commonOrder.includes(field)
    )

    return [...orderedFields, ...remainingFields]
  }

  // 필드명을 사용자 친화적으로 변환하는 함수
  const getFieldDisplayName = (fieldName: string): string => {
    const displayNames: { [key: string]: string } = {
      _id: 'ID',
      originalUrl: '원본 URL',
      shortCode: '단축 코드',
      isCustom: '커스텀 여부',
      title: '제목',
      description: '설명',
      favicon: '파비콘',
      userId: '사용자 ID',
      isPublic: '공개 여부',
      tags: '태그',
      category: '카테고리',
      notes: '메모',
      folderId: '폴더 ID',
      isFavorite: '즐겨찾기',
      isActive: '활성 상태',
      clickCount: '클릭 수',
      qrCode: 'QR 코드',
      createdAt: '생성일',
      updatedAt: '수정일',
      __v: '버전',
      sharedWith: '공유 대상',
      teamId: '팀 ID',
      teamPermissions: '팀 권한',
      username: '사용자명',
      email: '이메일',
      password: '비밀번호',
      avatar: '아바타',
      isVerified: '인증 여부',
      subscription: '구독',
      settings: '설정',
    }

    return displayNames[fieldName] || fieldName
  }

  // 필드 값을 적절히 포맷팅하는 함수
  const formatFieldValue = (fieldName: string, value: any): string => {
    // null 또는 undefined 처리
    if (value === null || value === undefined) {
      return '-'
    }

    // 불린 값 처리
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗'
    }

    // 날짜 처리
    if (fieldName.includes('At') || fieldName.includes('Date')) {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return formatDateTime(date)
        }
      } catch {
        // 날짜 파싱 실패 시 원본 값 표시
      }
    }

    // 배열 처리
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]'
      }
      return `[${value.length}개 항목]`
    }

    // 객체 처리
    if (typeof value === 'object') {
      const jsonStr = JSON.stringify(value, null, 2)
      if (jsonStr.length > 100) {
        return jsonStr.substring(0, 100) + '...'
      }
      return jsonStr
    }

    // 문자열 길이 제한
    const stringValue = String(value)
    if (stringValue.length > 50) {
      return stringValue.substring(0, 50) + '...'
    }

    return stringValue
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 max-h-[90vh] w-full max-w-6xl rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {collectionName} 컬렉션 데이터
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[calc(90vh-8rem)] overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                데이터 로딩 중...
              </span>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {data && !loading && (
            <>
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                총 {data.totalDocuments.toLocaleString()}개 문서 (페이지{' '}
                {data.pagination.page} / {data.pagination.totalPages})
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {fieldOrder.map(fieldName => (
                        <th
                          key={fieldName}
                          className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300"
                        >
                          {getFieldDisplayName(fieldName)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                    {data.data.map((item: any, index: number) => (
                      <tr
                        key={item._id || index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {fieldOrder.map(fieldName => {
                          const value = item[fieldName]
                          return (
                            <td
                              key={fieldName}
                              className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100"
                            >
                              {formatFieldValue(fieldName, value)}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!data.pagination.hasPrev}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  이전 페이지
                </button>

                <span className="text-sm text-gray-600 dark:text-gray-400">
                  페이지 {data.pagination.page} / {data.pagination.totalPages}
                </span>

                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!data.pagination.hasNext}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  다음 페이지
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CollectionDataModal
